import React, { useState, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import dayjs from "dayjs";
import DayjsUtils from "@date-io/dayjs";
import tzlookup from "tz-lookup";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { motion, AnimatePresence } from "framer-motion";
import { string, bool, func, arrayOf, object } from "prop-types";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import { createMuiTheme } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import updateLocale from "dayjs/plugin/updateLocale";

import gtm from "@gtm/core";
import RequestButton from "@components/shared/RequestButton";
import SimpleButton from "@components/shared/SimpleButton";
import Typography from "@components/shared/Typography";
import SlotPicker from "@components/SlotPicker/SlotPicker";
import ProgressBar from "../progressBar/ProgressBar";
import ContentContainer from "./ContentContainer";
import DatePickerTextField from "@components/TestDrive/TestDriveProgress/shared/DatePickerTextField";
import Radio from "@components/shared/Radio";

import { testDriveDateUpdateUrlEvent } from "@gtm/events/testdrive";
import { postDealAction } from "@redux/actions/dealActions";
import { fetchParkopolyAvailableSlots } from "@lib/test-drive/fetchParkopolyAvailableSlots";
import { color, tablet } from "@components/shared/utils";
import {
  useIsParkopolyEnabledSelector,
  useDaysBeforeTestDriveSelector,
} from "@redux/reducers/appConfig";
import { getIsProductionMode } from "@lib/getIsProductionMode";

import {
  CTA_TD_TYPES,
  DEAL_TYPE,
  TEST_DRIVE_TYPE_ID,
  DAYJS_LOCALES_MAP,
  DAYJS_LOCALES_WEEKDAY_TRANSLATIONS,
  LOCALES,
  COUNTRIES,
} from "@shared/constants";

import {
  setTestDrivePointAction,
  setTestDriveSlotAction,
} from "@redux/actions/testDriveActions";
import { getIsCountry } from "lib/getIsCountry";

const VALID_TIMES = {
  MORNING: "morning",
  AFTERNOON: "afternoon",
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

const DisplayedDate = styled(Typography).attrs({ size: "md" })`
  text-transform: capitalize;
`;

const RadioSelectContainer = styled(motion.div)`
  display: flex;
  font-weight: bold;

  label:first-child {
    margin-right: 0.5rem;
    margin-left: -0.5rem;
  }
`;

const CompleteStepButton = styled(RequestButton)`
  border: 1px solid ${color("darkGrey")};
  padding: 1rem 2rem;

  &:disabled {
    opacity: 0.4;
    color: ${color("darkGrey")};
  }

  &:hover:disabled {
    background-color: ${color("white")};
    cursor: not-allowed;
  }

  ${tablet`
    padding: 1rem 2.2rem;
  `}
`;

const CalendarContainer = styled.div`
  min-height: 30rem;
`;

const DateSummary = styled(motion.div)`
  margin: 1.5rem 0;
`;

const SlotsUnavailableTypography = styled(Typography)`
  margin-bottom: 2rem;
`;

const SpaceWrapper = styled.div`
  min-height: 4rem;
  display: flex;
  align-items: center;
`;

const defaultInitialDates = Array(60)
  .fill(null)
  .map((_, i) => ({
    dailySlotPeriodList: [{ driversSlotsAvailable: 1 }],
    date: dayjs().add(i, "day"),
  }));

dayjs.updateLocale(DAYJS_LOCALES_MAP[LOCALES.PT], {
  weekdaysMin: DAYJS_LOCALES_WEEKDAY_TRANSLATIONS[LOCALES.PT],
});

const PickDateStep = ({
  affiliation,
  progressBarColor,
  isStepComplete,
  setIsStepComplete,
  isFinalStepComplete,
  testDriveType,
  resetProgress,
  initialSelectedSlot,
  testDriveLocation,
  testDriveExclusionDates,
  parkopolyAvailableSlots: initialAvailableDays,
  isSSRRendered,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const router = useRouter();
  const isParkopolyEnabled = useIsParkopolyEnabledSelector();
  const {
    monday: daysBeforeTDMonday,
    tuesday: daysBeforeTDTuesday,
    wednesday: daysBeforeTDWednesday,
    thursday: daysBeforeTDThursday,
    friday: daysBeforeTDFriday,
    saturday: daysBeforeTDSaturday,
    sunday: daysBeforeTDSunday,
  } = useDaysBeforeTestDriveSelector();
  const isProduction = getIsProductionMode();

  const testDriveLocationTimezone = tzlookup(
    testDriveLocation.geoLatitude,
    testDriveLocation.geoLongitude
  );

  const currentTimeForTimezone = dayjs().tz(testDriveLocationTimezone);

  const materialTheme = createMuiTheme({
    palette: {
      primary: {
        main: theme.colors.amiOrange,
      },
    },
  });

  const getIsTimeOptionDisabled = (day, timeOption) => {
    if (isParkopolyEnabled) {
      return false;
    }

    if (dayjs(currentTimeForTimezone).isAfter(day.date, "day")) {
      return true;
    }

    if (!dayjs(currentTimeForTimezone).isSame(day.date, "day")) {
      return false;
    }

    if (timeOption === VALID_TIMES.MORNING) {
      return dayjs(currentTimeForTimezone).isAfter(
        dayjs(day.date)
          .hour(12)
          .minute(0)
          .second(0)
      );
    }

    if (timeOption === VALID_TIMES.AFTERNOON) {
      return dayjs(currentTimeForTimezone).isAfter(
        dayjs(day.date)
          .hour(20)
          .minute(0)
          .second(0)
      );
    }

    return false;
  };

  const daysConfig = currentTimeForTimezone => {
    const currentDay = dayjs(currentTimeForTimezone).day() || dayjs().day();

    let daysBeforeTD;

    switch (currentDay) {
      case 0:
        daysBeforeTD = daysBeforeTDSunday;
        break;
      case 1:
        daysBeforeTD = daysBeforeTDMonday;
        break;
      case 2:
        daysBeforeTD = daysBeforeTDTuesday;
        break;
      case 3:
        daysBeforeTD = daysBeforeTDWednesday;
        break;
      case 4:
        daysBeforeTD = daysBeforeTDThursday;
        break;
      case 5:
        daysBeforeTD = daysBeforeTDFriday;
        break;
      case 6:
        daysBeforeTD = daysBeforeTDSaturday;
        break;
      default:
        daysBeforeTD = 1;
    }
    return daysBeforeTD;
  };

  const initialSelectedDay = isParkopolyEnabled
    ? initialSelectedSlot &&
      initialAvailableDays?.find(day =>
        dayjs(initialSelectedSlot.startDateTime).isSame(dayjs(day.date), "day")
      )
    : initialSelectedSlot?.startDateTime;

  const initialDay = isParkopolyEnabled
    ? initialSelectedDay ??
      initialAvailableDays.find(day =>
        day.dailySlotPeriodList.some(slot => slot.driversSlotsAvailable > 0)
      )
    : {
        date:
          initialSelectedDay ??
          dayjs().add(daysConfig(currentTimeForTimezone) + 1, "day"),
      };

  const [previewAvailableDays, setPreviewAvailableDays] = useState(
    initialAvailableDays ?? defaultInitialDates
  );

  const [availableDays, setAvailableDays] = useState(
    initialAvailableDays ?? defaultInitialDates
  );

  const isDayExcluded = date => {
    return !!(testDriveExclusionDates ?? []).find(excludedDay =>
      dayjs(excludedDay).isSame(date, "day")
    );
  };

  const getNextAvailableDay = day => {
    const nextDay = day.date.add(1, "day");

    if (isDayExcluded(nextDay)) {
      return getNextAvailableDay({ date: nextDay });
    }

    return nextDay;
  };

  const modifiedInitialDay = () => {
    const day =
      getIsTimeOptionDisabled(initialDay, VALID_TIMES.AFTERNOON) &&
      !isParkopolyEnabled
        ? {
            date: initialDay.date.add(
              daysConfig(currentTimeForTimezone) + 1,
              "day"
            ),
          }
        : initialDay;

    if (isDayExcluded(day.date)) {
      return {
        date: getNextAvailableDay(day),
      };
    }

    return day;
  };

  const [selectedDay, setSelectedDay] = useState(modifiedInitialDay());
  const [selectedSlot, setSelectedSlot] = useState(initialSelectedSlot);
  const [isModifiedProgressDelay, setModifiedProgressDelay] = useState(false);

  let isLaMaisonWithoutEmail = false;
  if (testDriveLocation.type === TEST_DRIVE_TYPE_ID.LAMAISON) {
    if (getIsCountry(COUNTRIES.GB)) {
      isLaMaisonWithoutEmail = false;
    } else if (!testDriveLocation.email) {
      isLaMaisonWithoutEmail = true;
    }
  }

  const shouldDisableDate = date => {
    if (isDayExcluded(date)) {
      return true;
    }

    const targetAvailableDay = previewAvailableDays.find(previewAvailableDay =>
      date.isSame(dayjs(previewAvailableDay.date), "day")
    );

    if (!targetAvailableDay) {
      return true;
    }

    if (dayjs().isSame(date, "day")) {
      return true;
    }

    if (!isParkopolyEnabled) {
      const maximumDate = dayjs().add(
        daysConfig(currentTimeForTimezone),
        "days"
      );

      if (maximumDate.isAfter(date)) {
        return true;
      }

      return (
        dayjs(currentTimeForTimezone).isSame(date, "day") &&
        dayjs(currentTimeForTimezone).isAfter(dayjs(date).hour("18"), "hour")
      );
    }

    return !targetAvailableDay.dailySlotPeriodList.some(
      slot => slot.driversSlotsAvailable > 0
    );
  };

  const onMonthChange = async nextDate => {
    try {
      const nextFormattedDate = nextDate.format("YYYY-MM-DD");

      if (isParkopolyEnabled) {
        const data = await fetchParkopolyAvailableSlots(
          { location: testDriveLocation },
          { startDate: nextFormattedDate }
        );

        setPreviewAvailableDays(data);
      }
    } catch (error) {
      if (!isProduction) {
        console.error(error);
      }
    }
  };

  const onDayChange = date => {
    const targetAvailableDay = previewAvailableDays.find(previewAvailableDay =>
      date.isSame(dayjs(previewAvailableDay.date), "day")
    );

    setSelectedDay(targetAvailableDay);
    setAvailableDays(previewAvailableDays);
    setSelectedSlot(null);
  };

  const onDatePickerClose = () => {
    const selectedMonth = dayjs(selectedDay.date).month();

    if (availableDays.some(day => dayjs(day.date).month() === selectedMonth)) {
      return;
    }

    setPreviewAvailableDays(availableDays);
  };

  const onContinueButtonClick = async () => {
    dispatch(setTestDrivePointAction(testDriveLocation));
    dispatch(setTestDriveSlotAction(selectedSlot));

    return dispatch(
      postDealAction(CTA_TD_TYPES.DATE_SELECTED, DEAL_TYPE.TEST_DRIVE)
    );
  };

  const onContinueButtonClickSuccess = () => {
    setIsStepComplete(true);
  };

  const onModifyButtonClick = () => {
    setIsStepComplete(false);

    if (resetProgress) {
      resetProgress();
    }
  };

  const onTimeSpanRadioClick = e => {
    setSelectedSlot({
      name: e.target.value,
      timeLabel: e.target.name,
      startDateTime: dayjs(selectedDay.date).toISOString(),
    });
  };

  useEffect(() => {
    gtm.fire(
      testDriveDateUpdateUrlEvent({
        type: testDriveType,
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );
  }, []);

  useEffect(() => {
    setModifiedProgressDelay(true);
  }, [selectedDay?.date]);

  return (
    <section>
      <ProgressBar
        progressBarColor={progressBarColor}
        circleDelay={isModifiedProgressDelay && isStepComplete ? 0 : 1.2}
        isComplete={isStepComplete}
      />
      <motion.div
        key="pick-date-step"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <ContentContainer
          progressBarColor={progressBarColor}
          title={t("testDrive.dateAndHour")}
        >
          <AnimatePresence>
            {(isParkopolyEnabled && !initialDay) ||
            (!isParkopolyEnabled && isLaMaisonWithoutEmail) ? (
              <SlotsUnavailableTypography>
                {t("testDrive.slotsUnavailable")}
              </SlotsUnavailableTypography>
            ) : (
              <>
                {!isStepComplete && (
                  <CalendarContainer>
                    <ThemeProvider theme={materialTheme}>
                      <MuiPickersUtilsProvider
                        utils={DayjsUtils}
                        locale={DAYJS_LOCALES_MAP[i18n.language]}
                      >
                        <DatePicker
                          autoOk
                          disablePast
                          disableToolbar
                          variant="inline"
                          format="DD/MM/YYYY"
                          shouldDisableDate={shouldDisableDate}
                          value={dayjs(selectedDay.date)}
                          onMonthChange={onMonthChange}
                          onChange={onDayChange}
                          onClose={onDatePickerClose}
                          TextFieldComponent={DatePickerTextField}
                        />
                      </MuiPickersUtilsProvider>
                    </ThemeProvider>
                    {isParkopolyEnabled ? (
                      <SlotPicker
                        selectedSlot={selectedSlot}
                        onSlotChange={setSelectedSlot}
                        slots={selectedDay.dailySlotPeriodList}
                      />
                    ) : (
                      <SpaceWrapper>
                        <AnimatePresence>
                          {selectedDay?.date && (
                            <RadioSelectContainer
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Radio
                                label={t("testDrive.morning")}
                                name={t("testDrive.morning")}
                                checked={
                                  selectedSlot?.name === VALID_TIMES.MORNING
                                }
                                value={VALID_TIMES.MORNING}
                                onChange={onTimeSpanRadioClick}
                                disabled={getIsTimeOptionDisabled(
                                  selectedDay,
                                  VALID_TIMES.MORNING
                                )}
                                data-id="test-drive-radio-button-morning"
                              />
                              <Radio
                                label={t("testDrive.afternoon")}
                                name={t("testDrive.afternoon")}
                                value={VALID_TIMES.AFTERNOON}
                                checked={
                                  selectedSlot?.name === VALID_TIMES.AFTERNOON
                                }
                                onChange={onTimeSpanRadioClick}
                                disabled={getIsTimeOptionDisabled(
                                  selectedDay,
                                  VALID_TIMES.AFTERNOON
                                )}
                                data-id="test-drive-radio-button-afternoon"
                              />
                            </RadioSelectContainer>
                          )}
                        </AnimatePresence>
                      </SpaceWrapper>
                    )}
                    <CompleteStepButton
                      inverted
                      disabled={!selectedSlot}
                      data-id="test-drive-complete-button"
                      triggerRequestCallback={onContinueButtonClick}
                      onRequestSuccess={onContinueButtonClickSuccess}
                    >
                      {t("testDrive.continue")}
                    </CompleteStepButton>
                  </CalendarContainer>
                )}
                {isStepComplete && (
                  <DateSummary
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.5 }}
                  >
                    {isParkopolyEnabled ? (
                      <DisplayedDate data-id="test-drive-displayed-date">
                        {`${dayjs(selectedDay.date)
                          .locale(DAYJS_LOCALES_MAP[i18n.language])
                          .format("LL")} ${selectedSlot?.name.replace(
                          /\s/g,
                          "-"
                        ) ?? null}`}
                      </DisplayedDate>
                    ) : (
                      <DisplayedDate data-id="test-drive-displayed-date">
                        {`${dayjs(selectedDay.date)
                          .locale(DAYJS_LOCALES_MAP[i18n.language])
                          .format("LL")} ${t(
                          `testDrive.${selectedSlot.name}`
                        )}`}
                      </DisplayedDate>
                    )}
                    {!isFinalStepComplete && (
                      <SimpleButton
                        withArrow
                        onClick={onModifyButtonClick}
                        data-id="test-drive-modify-pick-date"
                      >
                        {t("general.modify")}
                      </SimpleButton>
                    )}
                  </DateSummary>
                )}
              </>
            )}
          </AnimatePresence>
        </ContentContainer>
      </motion.div>
    </section>
  );
};

PickDateStep.propTypes = {
  affiliation: string,
  isFinalStepComplete: bool.isRequired,
  isStepComplete: bool.isRequired,
  progressBarColor: string,
  resetProgress: func,
  setIsStepComplete: func.isRequired,
  testDriveType: string.isRequired,
  parkopolyAvailableSlots: arrayOf(object),
  testDriveLocation: object,
  testDriveExclusionDates: arrayOf(string),
  initialSelectedSlot: object,
  isSSRRendered: bool,
};

export default PickDateStep;
